import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import ModuleKanbanMenu from '../components/ModuleKanbanMenu';
import {
  LogOut,
  DollarSign,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Building2,
  Mail,
  Phone,
  Calendar,
  Download,
  Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinanzasPage = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchSales();
  }, [navigate]);

  const fetchSales = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/sales`;
      const params = new URLSearchParams();
      
      if (filter && filter !== 'all') {
        params.append('status', filter);
      }
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data);
    } catch (error) {
      console.error('Error fetching sales:', error);
      toast.error('Error al cargar las ventas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      let url = `${API}/sales/export/excel`;
      const params = new URLSearchParams();
      
      if (filter && filter !== 'all') {
        params.append('status', filter);
      }
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `ventas_analytica_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Archivo Excel descargado exitosamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar a Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const handleApplyFilters = () => {
    fetchSales();
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setFilter('all');
    // Fetch without filters
    setTimeout(() => fetchSales(), 100);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Sesión cerrada');
  };

  const filteredSales = sales;

  const stats = {
    total: sales.length,
    completed: sales.filter(s => s.payment_status === 'completed').length,
    pending: sales.filter(s => s.payment_status === 'pending' || s.payment_status === 'pending_verification').length,
    totalAmount: sales
      .filter(s => s.payment_status === 'completed')
      .reduce((sum, s) => sum + s.amount, 0)
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" /> Completado
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" /> Pendiente
          </span>
        );
      case 'pending_verification':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" /> Por Verificar
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" /> Fallido
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#2F3D8E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Panel de Finanzas</h1>
            <p className="text-white/70 text-sm">Bienvenido, {user?.username}</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-white/30 text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </header>

      <ModuleKanbanMenu user={user} active="finanzas" />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#2F3D8E]/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#2F3D8E]" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Total Ventas</p>
                <p className="text-2xl font-bold text-[#2F3D8E]">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Completadas</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#4C8C3B]/10 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#4C8C3B]" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Total Recaudado</p>
                <p className="text-2xl font-bold text-[#4C8C3B]">${stats.totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Refresh */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-[#2F3D8E]" />
            <h3 className="text-lg font-semibold text-[#2F3D8E]">Filtros</h3>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4 mb-4">
            {/* Date From */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Fecha Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-10"
              />
            </div>
            
            {/* Date To */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Fecha Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-10"
              />
            </div>
            
            {/* Status Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Estado</Label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#5DA5D8]"
              >
                <option value="all">Todas</option>
                <option value="completed">Completadas</option>
                <option value="pending">Pendientes PayPal</option>
                <option value="pending_verification">Por Verificar</option>
              </select>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">&nbsp;</Label>
              <div className="flex gap-2">
                <Button
                  onClick={handleApplyFilters}
                  disabled={isLoading}
                  className="bg-[#2F3D8E] hover:bg-[#1a2456] text-white h-10"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="h-10"
                >
                  Limpiar
                </Button>
              </div>
            </div>
          </div>
          
          {/* Export Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleExportExcel}
              disabled={isExporting || sales.length === 0}
              className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white"
            >
              {isExporting ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Exportando...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Descargar Excel</>
              )}
            </Button>
          </div>
        </div>

        {/* Sales Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Voucher</th>
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Método</th>
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Empresa</th>
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Proyecto</th>
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Contacto</th>
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Monto</th>
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Fecha</th>
                  <th className="text-left px-6 py-4 font-semibold text-[#2F3D8E]">Estado</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-gray-400">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                      Cargando ventas...
                    </td>
                  </tr>
                ) : filteredSales.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-gray-400">
                      <FileText className="w-8 h-8 mx-auto mb-2" />
                      No hay ventas registradas
                    </td>
                  </tr>
                ) : (
                  filteredSales.map((sale) => (
                    <tr key={sale.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-mono font-bold text-[#4C8C3B]">
                          {sale.voucher_number}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {sale.payment_method === 'transfer' ? (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              🏦 Transferencia
                            </span>
                            {sale.transfer_bank && (
                              <p className="text-xs text-gray-500 mt-1">{sale.transfer_bank}</p>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                            💳 PayPal
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{sale.company_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{sale.project_name}</td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            {sale.contact_email}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            {sale.contact_phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-lg font-bold text-[#2F3D8E]">
                          ${sale.amount.toLocaleString()}
                        </span>
                        <span className="text-gray-400 text-sm ml-1">{sale.currency}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {new Date(sale.created_at).toLocaleDateString('es-EC')}
                        </div>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(sale.payment_status)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FinanzasPage;
